<template>
  <div class="row">
    <div class="col-12 py-3 sticky-top bg-white">
      <div class="row">
        <div class="col-6">
          <button class="btn btn-primary" @click="save">
            {{ 'Save' | translate }}
          </button>
        </div>
        <div class="col-6 text-end">
          <a href="/account/page/edit" class="btn btn-outline-primary">
            {{ 'Edit page content' | translate }}
          </a>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'Settings' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="page-name">{{ 'Page name' | translate }}*</label>
            <input
              id="page-name"
              name="name"
              class="form-control"
              type="text"
              :placeholder="$t('example: Skyier')"
              v-model="page.name"
            />
            <InputErrors name="name"></InputErrors>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="domain">{{ 'Domain' | translate }}*</label>
            <input
              id="domain"
              name="domain"
              class="form-control"
              type="text"
              :placeholder="$t('example: courses.skyier.com')"
              v-model="page.domain"
            />
            <InputErrors name="domain"></InputErrors>
            <small class="form-text text-muted">
              {{ 'Remember to redirect your domain first.' | translate }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="locale">{{ 'Page language' | translate }}</label>
            <select
              name="locale"
              class="form-control"
              id="locale"
              v-model="page.locale"
            >
              <option value="pl">{{ 'Polish' | translate }}</option>
              <option value="en">{{ 'English' | translate }}</option>
            </select>
            <small class="form-text text-muted">
              {{ 'Page version for students.' | translate }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="currency">{{ 'Currency' | translate }}</label>
            <select
              name="currency"
              class="form-control"
              id="currency"
              v-model="page.currency"
            >
              <option value="USD">USD</option>
              <option value="PLN">PLN</option>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
              <option value="CHF">CHF</option>
              <option value="NOK">NOK</option>
            </select>
            <small class="form-text text-muted">
              {{
                'The currency your students will use to pay for the courses.'
                  | translate
              }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">{{ 'Features' | translate }}</p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="display_articles"
                name="display_articles"
                v-model="page.display_articles"
              />
              <label class="form-check-label" for="display_articles">
                {{ 'Enable articles feature' | translate }}
              </label>
            </div>
          </div>

          <div
            class="mb-3 col-12 col-md-8 offset-md-2"
            v-if="page.display_articles"
          >
            <label for="articles_display_name">
              {{ 'Articles link' | translate }}
            </label>

            <input
              id="articles_display_name"
              name="articles_display_name"
              class="form-control"
              type="text"
              :placeholder="$t('example: blog')"
              v-model="page.articles_display_name"
            />
            <InputErrors name="articles_display_name"></InputErrors>
            <small class="form-text text-muted">
              {{
                'The name of your articles page in top navigation.' | translate
              }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="display_welcome_message"
                name="display_welcome_message"
                v-model="page.options.display_welcome_message"
              />
              <label class="form-check-label" for="display_welcome_message">
                {{ 'Display welcome message' | translate }}
                <br />
                <small class="text-muted">
                  {{ 'Free tier users or one-time buyers' | translate }}.
                </small>
              </label>
            </div>
          </div>

          <div
            class="mb-3 col-12 col-md-8 offset-md-2"
            v-if="page.options.display_welcome_message"
          >
            <div>
              <QuillInputSv2
                :value="page.options.welcomeMessage"
                @quillChanged="changed('welcomeMessage', ...arguments)"
              ></QuillInputSv2>
            </div>
            <div class="mt-3">
              <label for="">
                <small>{{ 'Additional HTML' | translate }}</small>
              </label>
              <HtmlEditor
                :value="
                  page.options.welcomeMessage
                    ? page.options.welcomeMessage.additionalHtml
                    : ''
                "
                @change="changed('welcomeMessage.additionalHtml', ...arguments)"
              ></HtmlEditor>
            </div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="display_welcome_message_for_subscribers"
                name="display_welcome_message_for_subscribers"
                v-model="page.options.display_welcome_message_for_subscribers"
              />
              <label
                class="form-check-label"
                for="display_welcome_message_for_subscribers"
              >
                {{ 'Display welcome message for subscribers' | translate }}
              </label>
            </div>
          </div>

          <div
            class="mb-3 col-12 col-md-8 offset-md-2"
            v-if="page.options.display_welcome_message_for_subscribers"
          >
            <div>
              <QuillInputSv2
                :value="page.options.welcomeMessageForSubscribers"
                @quillChanged="
                  changed('welcomeMessageForSubscribers', ...arguments)
                "
              ></QuillInputSv2>
            </div>
            <div class="mt-3">
              <label for="">
                <small>{{ 'Additional HTML' | translate }}</small>
              </label>
              <HtmlEditor
                :value="
                  page.options.welcomeMessageForSubscribers
                    ? page.options.welcomeMessageForSubscribers.additionalHtml
                    : ''
                "
                @change="
                  changed(
                    'welcomeMessageForSubscribers.additionalHtml',
                    ...arguments,
                  )
                "
              ></HtmlEditor>
            </div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="semi_manual_invoice"
                name="semi_manual_invoice"
                v-model="page.options.semi_manual_invoice"
              />

              <label class="form-check-label" for="semi_manual_invoice">
                {{
                  'I want to create invoices manually (by clicking on a button)'
                    | translate
                }}
              </label>
            </div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="display_promo_code_field"
                name="display_promo_code_field"
                v-model="page.display_promo_code_field"
              />

              <label class="form-check-label" for="display_promo_code_field">
                {{ 'Display promo code field in checkout' | translate }}
              </label>
            </div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="external_sales_pages"
                name="external_sales_pages"
                v-model="page.external_sales_pages"
              />

              <label class="form-check-label" for="external_sales_pages">
                {{ 'Use external sales pages' | translate }}
              </label>
            </div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="display_additional_nav_links"
                name="display_additional_nav_links"
                v-model="page.options.display_additional_nav_links"
              />

              <label
                class="form-check-label"
                for="display_additional_nav_links"
              >
                {{ 'Display additional navigation links' | translate }}
              </label>
            </div>
          </div>

          <div
            class="col-12 col-md-8 offset-md-2"
            v-if="page.options.display_additional_nav_links"
          >
            <div
              class="mb-3"
              v-for="(link, index) in page.options.navigation"
              :key="index"
            >
              <div class="row">
                <div class="col-5">
                  <input
                    class="form-control"
                    placeholder="https://example.com"
                    v-model="link.url"
                  />
                  <InputErrors
                    :name="`options.navigation.${index}.url`"
                  ></InputErrors>
                </div>
                <div class="col-5">
                  <input
                    class="form-control"
                    :placeholder="$t('Title')"
                    v-model="link.title"
                  />
                  <InputErrors
                    :name="`options.navigation.${index}.title`"
                  ></InputErrors>
                </div>
                <div class="col-2">
                  <button
                    class="btn btn-outline-secondary"
                    @click="removeNavLink(index)"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                </div>
              </div>
            </div>
            <div>
              <button class="btn btn-outline-primary" @click="addNewNavLink">
                {{ 'Add' | translate }}
              </button>
            </div>
            <div><hr /></div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="display_custom_checkout_checkboxes"
                name="display_custom_checkout_checkboxes"
                v-model="page.options.display_custom_checkout_checkboxes"
              />

              <label
                class="form-check-label"
                for="display_custom_checkout_checkboxes"
              >
                {{ 'Display custom checkout checkboxes' | translate }}
                {{ '(instead of default one)' | translate }}
              </label>
            </div>
            <div class="text-muted">
              <small>
                {{ 'All checkboxes will be required' | translate }}.
                {{
                  'If you want to include a link, use this code:' | translate
                }}
                &lt;a href="/example" target="_blank"&gt;example&lt;/a&gt;
              </small>
            </div>
          </div>

          <div
            class="col-12 col-md-8 offset-md-2"
            v-if="page.options.display_custom_checkout_checkboxes"
          >
            <div
              class="mb-3"
              v-for="(item, index) in page.options.custom_checkout_checkboxes"
              :key="index"
            >
              <div class="row">
                <div class="col-10">
                  <textarea
                    class="form-control"
                    :placeholder="$t('Checkbox text here')"
                    v-model="item.text"
                  ></textarea>
                  <InputErrors
                    :name="`options.custom_checkboxes.${index}.text`"
                  ></InputErrors>
                </div>
                <div class="col-2">
                  <button
                    class="btn btn-outline-secondary"
                    @click="removeCustomCheckbox(index)"
                  >
                    <i class="fas fa-minus"></i>
                  </button>
                </div>
              </div>
            </div>
            <div>
              <button
                class="btn btn-outline-primary"
                @click="addNewCustomCheckbox"
              >
                {{ 'Add' | translate }}
              </button>
            </div>
            <div><hr /></div>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="simplified_checkout_page"
                name="simplified_checkout_page"
                v-model="page.options.simplified_checkout_page"
              />

              <label class="form-check-label" for="simplified_checkout_page">
                {{ 'Display simplified checkout page' | translate }}
              </label>
            </div>
            <div class="text-muted">
              <small>
                {{
                  'Only name, email and password will be required' | translate
                }}.
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'Payment model' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="single"
                name="single"
                v-model="page.single"
              />
              <label class="form-check-label" for="single">
                {{ 'One time payment model' | translate }}
              </label>
              <br />
              <small class="text-muted">
                {{ 'Single payments are enabled by default' | translate }}
              </small>
            </div>
          </div>
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="membership"
                name="membership"
                v-model="page.membership"
              />
              <label class="form-check-label" for="membership">
                {{ 'Membership model' | translate }}
                <a href="/account/page/membership">
                  {{ 'Options' | translate }}
                </a>
              </label>
              <br />
              <small class="text-muted">
                {{
                  'Membership model lets you sell your courses as a subscription. Works only with selected payment providers.'
                    | translate
                }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'Notifications' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="transaction_notification"
                name="transaction_notification"
                v-model="page.options.transaction_notification"
              />
              <label class="form-check-label" for="transaction_notification">
                {{ 'Notify me about completed transactions' | translate }}
              </label>
            </div>
          </div>
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <div class="form-check">
              <input
                class="form-check-input"
                type="checkbox"
                value="1"
                id="comment_notification"
                name="comment_notification"
                v-model="page.options.comment_notification"
              />
              <label class="form-check-label" for="comment_notification">
                {{ 'Notify me about new comments' | translate }}
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'Author details' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="name">{{ 'Company name / Name' | translate }}*</label>
            <input
              id="name"
              name="author"
              class="form-control"
              type="text"
              v-model="page.author"
            />
            <InputErrors name="author"></InputErrors>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="author_email">{{ 'Email' | translate }}*</label>

            <input
              id="author_email"
              name="author_email"
              class="form-control"
              type="email"
              v-model="page.author_email"
            />
            <InputErrors name="author_email"></InputErrors>
            <small class="form-text text-muted">
              {{ 'This email will be publicly available.' | translate }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="copyright">{{ 'Copyright' | translate }}</label>
            <input
              id="copyright"
              name="copyright"
              class="form-control"
              type="text"
              v-model="page.copyright"
            />
            <InputErrors name="copyright"></InputErrors>
          </div>
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="copyright_link">
              {{ 'Copyright url' | translate }}
            </label>
            <input
              id="copyright_link"
              name="copyright_link"
              class="form-control"
              type="text"
              v-model="page.copyright_link"
            />
            <InputErrors name="copyright_link"></InputErrors>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'Legal' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="privacy">{{ 'Privacy' | translate }}</label>

            <textarea
              class="form-control"
              id="privacy"
              name="privacy"
              rows="3"
              v-model="page.privacy"
            ></textarea>
            <small class="form-text text-muted">
              {{ 'Separate using new line.' | translate }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="terms">{{ 'Terms' | translate }}</label>

            <textarea
              class="form-control"
              id="terms"
              name="terms"
              rows="3"
              v-model="page.terms"
            ></textarea>
            <small class="form-text text-muted">
              {{ 'Separate using new line.' | translate }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'Javascript' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="js">{{ 'Head JavaScript' | translate }}</label>

            <HtmlEditor
              :value="page.js"
              @change="changed('js', ...arguments)"
            ></HtmlEditor>

            <small class="form-text text-muted">
              {{
                '&lt;script&gt;&lt;script&gt;. Google Tag Manager' | translate
              }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="body_top_script">
              {{ 'Body Top Script' | translate }}
            </label>

            <HtmlEditor
              :value="page.body_top_script"
              @change="changed('body_top_script', ...arguments)"
            ></HtmlEditor>

            <small class="form-text text-muted">
              {{
                '&lt;script&gt;&lt;script&gt; or &lt;noscript&gt;&lt;noscript&gt;.'
                  | translate
              }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="col-12 my-3">
      <div class="card">
        <div class="card-header">
          <p class="lead m-0 text-uppercase">
            {{ 'SEO' | translate }}
          </p>
        </div>
        <div class="card-body row">
          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="seo_title">{{ 'SEO title' | translate }}</label>

            <input
              id="seo_title"
              name="seo_title"
              class="form-control"
              type="text"
              maxlength="70"
              v-model="page.seo_title"
            />
            <small class="form-text text-muted">
              {{ 'Max: 70 characters, including spaces.' | translate }}
            </small>
          </div>

          <div class="mb-3 col-12 col-md-8 offset-md-2">
            <label for="seo_description">
              {{ 'SEO description' | translate }}
            </label>

            <input
              id="seo_description"
              name="seo_description"
              class="form-control"
              type="text"
              maxlength="150"
              v-model="page.seo_description"
            />
            <small class="form-text text-muted">
              {{ 'Max: 150 characters, including spaces.' | translate }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InputErrors from '~/checkout/components/InputErrors'
import QuillInputSv2 from './QuillInputSv2'
import HtmlEditor from './HtmlEditor'
import _ from 'lodash'

export default {
  components: {
    InputErrors,
    QuillInputSv2,
    HtmlEditor,
  },
  computed: {
    errors() {
      return this.$store.state.errors
    },
    page() {
      return {
        ...this.$store.state.page,
        options: _.isEmpty(this.$store.state.page.options) ? {} : this.$store.state.page.options,
      }
    }
  },
  methods: {
    changed(field, html, quill) {
      if (field === 'welcomeMessage') {
        return (this.page.options.welcomeMessage = {
          html,
          quill: JSON.stringify(quill),
        })
      }
      if (field === 'welcomeMessageForSubscribers') {
        return (this.page.options.welcomeMessageForSubscribers = {
          html,
          quill: JSON.stringify(quill),
        })
      }
      if (field === 'welcomeMessage.additionalHtml') {
        return (this.page.options.welcomeMessage = {
          ...this.page.options.welcomeMessage,
          additionalHtml: html,
        })
      }

      if (field === 'welcomeMessageForSubscribers.additionalHtml') {
        return (this.page.options.welcomeMessageForSubscribers = {
          ...this.page.options.welcomeMessageForSubscribers,
          additionalHtml: html,
        })
      }

      return (this.page[field] = html)
    },
    addNewNavLink() {
      if (this.page.options.navigation) {
        this.page.options = {
          ...this.page.options,
          navigation: [
            ...this.page.options.navigation,
            {
              url: '',
              title: '',
            },
          ],
        }
      } else {
        this.page.options = {
          ...this.page.options,
          navigation: [
            {
              url: '',
              title: '',
            },
          ],
        }
      }
    },
    removeNavLink(index) {
      this.page.options.navigation.splice(index, 1)
    },
    removeCustomCheckbox(index) {
      this.page.options.custom_checkout_checkboxes.splice(index, 1)
    },
    addNewCustomCheckbox() {
      if (this.page.options.custom_checkout_checkboxes) {
        this.page.options = {
          ...this.page.options,
          custom_checkout_checkboxes: [
            ...this.page.options.custom_checkout_checkboxes,
            {
              text: '',
            },
          ],
        }
      } else {
        this.page.options = {
          ...this.page.options,
          custom_checkout_checkboxes: [
            {
              text: '',
            },
          ],
        }
      }
    },
    handleError(error) {
      if (error.response && error.response.status === 422) {
        this.$store.commit('errors', error.response.data.errors)
        iziToast.warning({
          title: 'Warning',
          message: this.$t('Validation error'),
          position: 'bottomLeft',
        })
      } else {
        iziToast.warning({
          title: 'Error',
          message: this.$t('Something went wrong'),
          position: 'bottomLeft',
        })
      }
    },
    displaySuccessMessage() {
      iziToast.success({
        title: 'OK',
        message: this.$t('Saved'),
        position: 'bottomLeft',
      })
    },
    save() {
      this.$store
        .dispatch('updatePage', this.page)
        .then(this.displaySuccessMessage)
        .catch(this.handleError)
    },
  },
}
</script>

<style></style>
